// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { ChannelType, Member, MemberRole, User, UserPlan } from '@/models';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/user-avatar';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import type { OnlineUser, OnlineUserDevice } from '@/components/presence/presence-list';
import { formatTimeAgo } from '@/lib/date-fns-format';
import { useParams } from 'react-router-dom';
import { API_BASE, GAME_POLLING_INTERVAL } from '@/config';
import { formatGameName } from '@/components/gaming/get-game-info.ts';
import { fetchWithPersistentCacheUnencrypted } from '@/utils/fetch-with-cache.ts';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { GlowingCardWrapper } from '@/components/server/glowing-card-member.tsx';
import { statusClasses } from '@/context/status-context/status-classes.ts';
import { useBreakpoint } from '@/hooks/use-breakpoint.ts';
import { Volume2 } from 'lucide-react';

interface ServerMemberProps {
  member: Member;
  ownerId: string;
  onKick: (id: string) => void;
  onRoleChange: (id: string, role: MemberRole) => void;
  onOpenContextMenu: (e: React.MouseEvent) => void;
  onlineStatus: UserStatus;
  description?: string;
}

export const ServerMember = ({
                               member,
                               ownerId,
                               onOpenContextMenu,
                               onlineStatus,
                               description,
                             }: ServerMemberProps) => {
  const { serverId, memberId } = useParams<{
    serverId: string;
    memberId: string;
  }>();
  const socket = useSocket();
  const [serverName, setServerName] = useState<string | undefined>(undefined);
  const [channelName, setChannelName] = useState<string | undefined>(undefined);
  const [lastSeen, setLastSeen] = useState<string | null>(null);
  const [playingGameProcessName, setPlayingGameProcessName] = useState<string | null>(null);
  const [playingGameDisplayName, setPlayingGameDisplayName] = useState<string | null>(null);
  const [gameIconUrl, setGameIconUrl] = useState<string | null>(null);
  const isActive = onlineStatus !== 'offline' && onlineStatus !== 'invisible';
  const [userFromDb, setUserFromDb] = useState<User | null>(null);
  const { isMd: isDesktop } = useBreakpoint('md');
  const [currentOnlineServerId, setCurrentOnlineServerId] = useState<string | undefined>();

  /**
   * via tpresence
   * @stable
   */
  useEffect(() => {
    if (!socket || !member) return;

    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === 'presence-update') {
        const updatedUser: OnlineUser | undefined = data.users.find(
          (user: OnlineUser) => user.userId === member.profileId
        );

        if (updatedUser) {
          const devices = updatedUser.devices || [];
          const devicesInVideoChannel = devices.filter(
            (device) => device.channelType !== ChannelType.TEXT
          );

          const selectedVideoDevice = devicesInVideoChannel[0];
          setServerName(selectedVideoDevice?.serverName);
          setChannelName(selectedVideoDevice?.channelName);
          setCurrentOnlineServerId(selectedVideoDevice?.serverId);
          setLastSeen(new Date().toISOString());
        } else {
          setServerName(undefined);
          setChannelName(undefined);
          setLastSeen(null);
        }
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, member.profileId]);

  /**
   * Logic when user started playing before newcomers open sidebar to see status
   * currentGame - game.exe
   * processName - null
   * game name - game
   */
  useEffect(() => {
    let isMounted = true;

    const pollCurrentGame = async () => {
      try {
        const result = await fetchCurrentGame(member.profileId);
        if (!isMounted) return;

        if (result) {
          const displayName = result.gameName || formatGameName(result.currentGame) || null;
          const processName = result.processName || result.currentGame || null;

          // Обновляем только если что-то реально изменилось
          setPlayingGameDisplayName((prev) => (prev !== displayName ? displayName : prev));
          setPlayingGameProcessName((prev) => (prev !== processName ? processName : prev));
          setGameIconUrl((prev) => (prev !== result.iconUrl ? result.iconUrl : prev));
        }
      } catch (e) {
        console.error('Failed to poll current game:', e);
      }
    };

    pollCurrentGame();
    const intervalId = setInterval(pollCurrentGame, GAME_POLLING_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [member, member.profileId]);

  useEffect(() => {
    if (!member.profileId) {
      return;
    }

    fetch(`${API_BASE}/api/users/${member.profileId}`)
      .then((res) => res.json())
      .then((data) => {
        setUserFromDb(data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [member.profileId]);

  return (
    <GlowingCardWrapper admin={member.profileId == ownerId} plan={userFromDb?.plan}>
      <div onContextMenu={onOpenContextMenu} className="relative group min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onOpenContextMenu}
              className={cn(
                'relative group px-2 py-1 rounded-md flex items-center gap-x-2 w-full hover:bg-zinc-700/10 dark:hover:bg-zinc-700/50 transition duration-200',
                memberId === member.id && 'bg-zinc-700/20 dark:bg-zinc-700',
                // user.plan === UserPlan.OPTIMIM && 'bg-red-500/10 border-r-1 border-red-500/20',
                // member.role === UserPlan.EXTENDED && 'bg-blue-500/10 border-r-1 border-blue-500/20',
                !isActive && 'opacity-60 dark:opacity-60'
              )}
              title={
                onlineStatus === 'invisible' || onlineStatus === 'offline'
                  ? `Был в сети ${formatTimeAgo(new Date(lastSeen || 0))}`
                  : `В сети ${formatTimeAgo(new Date(lastSeen || 0))}`
              }
            >
              <div
                className={cn(
                  'relative size-6 md:size-8 rounded-full flex items-center justify-center animate-fadeIn',
                  statusClasses[onlineStatus]
                )}
              >
                <UserAvatar
                  src={member?.profile?.image}
                  name={
                    member?.profile?.nickname
                      ? member?.profile?.nickname
                      : member.profile.name || 'Unknown User'
                  }
                  alt={member?.profile?.name || 'User'}
                  className="size-6 rounded-full transition-all duration-300 group-hover:scale-110 md:size-7"
                />
                {onlineStatus !== 'invisible' && onlineStatus !== 'offline' && (
                  <div
                    className={cn(
                      'absolute bottom-0 right-0 size-3 rounded-full border border-white transition-all duration-300',
                      statusClasses[onlineStatus]
                    )}
                  />
                )}
                {onlineStatus === 'away' && (
                  <div className="absolute -top-1 -right-1 text-xs md:text-sm text-zinc-600 transition-all duration-300">
                    💤
                  </div>
                )}
              </div>
              <div className="animate-fadeIn flex flex-col items-start justify-start transition-all flex-1 min-w-0">
                <div className="flex items-center gap-x-2 min-w-0">
                  <p
                    className={cn(
                      'font-semibold text-sm text-left text-zinc-500 group-hover:text-zinc-600 dark:text-zinc-400 dark:group-hover:text-zinc-300 transition-all duration-300',
                      'w-full min-w-0 whitespace-normal break-words leading-snug',
                      memberId === member.id &&
                      'text-primary dark:text-zinc-200 dark:group-hover:text-white',
                      member.role === MemberRole.ADMIN &&
                      'text-rose-500 dark:text-rose-400 group-hover:text-rose-600 dark:group-hover:text-rose-50',
                      member.role === MemberRole.MODERATOR &&
                      'text-indigo-500 dark:text-indigo-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-50',
                      member.role === MemberRole.PRIVATE &&
                      'text-stone-500 dark:text-stone-400 group-hover:text-stone-600 dark:group-hover:text-stone-50',
                      isActive && onlineStatus === 'away' && 'opacity-80 dark:opacity-70',
                      // memberId === member.id && onlineStatus === 'invisible' && 'opacity-100 dark:opacity-100',
                      //    TODO: IF Me - opacity 100
                    )}
                  >
                    {member?.profile?.nickname
                      ? member?.profile?.nickname
                      : member.profile.name || 'Unknown User'}
                    {member.profileId == ownerId && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs"> 👑</span>
                        </TooltipTrigger>
                        <TooltipContent className="bg-stone-900 text-white hidden md:block">
                          Владелец сервера
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <span className="size-2 text-xs shrink-0 transition-all duration-300">
                      {['😀', '☹️', '🎮', '💡'].includes(onlineStatus) ? onlineStatus : ''}
                    </span>
                  </p>
                </div>
                {description && (
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">{description}</span>
                )}
                {onlineStatus !== 'invisible' &&
                  onlineStatus !== 'offline' &&
                  serverName &&
                  channelName && (
                    <div className="animate-fadeIn flex flex-col gap-1 text-xs text-zinc-400 dark:text-zinc-500 min-w-0">
                      <div className="flex items-center gap-1 min-w-0 w-full">
                        {/*<div className="flex items-center gap-1 min-w-0 overflow-hidden">*/}
                        <Volume2 className="h-3 w-3 shrink-0 text-green-500" strokeWidth={2.5} />
                        <span className="truncate text-green-500">
                          {serverId === currentOnlineServerId ? channelName : serverName}
                        </span>
                      </div>
                    </div>
                  )}
                {onlineStatus !== 'invisible' &&
                  onlineStatus !== 'offline' &&
                  (playingGameDisplayName || playingGameProcessName) && (
                    <div
                      key={playingGameDisplayName}
                      className="relative flex flex-row items-center justify-start gap-1 text-sm text-zinc-500 dark:text-zinc-500 animate-fadeIn"
                    >
                      {gameIconUrl ? (
                        <img
                          src={gameIconUrl}
                          alt={playingGameDisplayName || 'Game Play'}
                          className="h-4 w-auto rounded"
                        />
                      ) : (
                        <div className="h-4 w-4 rounded bg-gray-300" />
                      )}
                      <span>{playingGameDisplayName}</span>
                    </div>
                  )}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent className="bg-stone-900 text-white hidden md:block">
            {member.role === MemberRole.ADMIN && 'Admin'}
            {member.role === MemberRole.MODERATOR && 'Moderator'}
            {member.role === MemberRole.PRIVATE && 'Private'}
            {member.role === MemberRole.GUEST && 'Guest'}
          </TooltipContent>
        </Tooltip>
      </div>
    </GlowingCardWrapper>
  );
};

type UserStatus = 'online' | 'dnd' | 'away' | 'invisible' | 'offline' | '😀' | '☹️' | '🎮' | '💡';

export const planGlowClasses: Record<UserPlan, string> = {
  [UserPlan.FREE]: '',
  [UserPlan.BASIC]: 'shadow-[0_0_6px_rgba(59,130,246,0.6)]', // слабое синее свечение
  [UserPlan.OPTIMIM]: 'shadow-[0_0_9px_rgba(16,185,129,0.7)]', // среднее зеленое
  [UserPlan.EXTENDED]: 'shadow-[0_0_11px_rgba(225,80,225,0.9)]', // сильное розовое
};

export const planCardClasses: Record<UserPlan, string> = {
  [UserPlan.FREE]: '',
  [UserPlan.BASIC]: 'bg-blue-500/10', // слабое синее свечение
  [UserPlan.OPTIMIM]: 'bg-green-400/20', // среднее зеленое
  [UserPlan.EXTENDED]: 'bg-purple-500/30', // сильное розовое
};

/**
 * @returns
 * {
 *       processName,
 *       currentGame , // Overwatch.exe this one contains process
 *       lastActive,
 *       gameName, // Overwatch 2
 *       iconUrl,
 *       steamAppId,
 * }
 * @param userId
 */
export const fetchCurrentGame = async (userId: string) => {
  try {
    const res = await fetchWithPersistentCacheUnencrypted(
      `${API_BASE}/api/users/status/currentgame?userId=${userId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
      60_000
    );
    if (!res) throw new Error('Failed to fetch game status');
    const data = res;

    return {
      processName: data.processName, //(null)
      currentGame: data.currentGame, // Overwatch.exe this one contains process
      lastActive: data.lastActive,
      gameName: data.gameName, // Overwatch 2
      iconUrl: data.iconUrl,
      steamAppId: data.steamAppId,
    };
  } catch (error) {
    console.error('Failed to fetch current game:', error);
    return null;
  }
};

/**
 * Returns the deterministic "latest" device based on lastSeen + priority
 */
export function getLatestDeviceDeterministic(devices: OnlineUserDevice[]): OnlineUserDevice | null {
  if (!devices || devices.length === 0) return null;

  return devices.reduce((latest, current) => {
    if (!latest.lastSeen) return current;
    if (!current.lastSeen) return latest;

    if (current.lastSeen > latest.lastSeen) {
      return current;
    } else if (current.lastSeen === latest.lastSeen) {
      return current.deviceId > latest.deviceId ? current : latest; // deterministic tie-breaker: higher deviceId wins
      // return typePriority[current.type] > typePriority[latest.type] ? current : latest;
    } else {
      return latest;
    }
  });
}
